import {startTransition, useCallback, useMemo} from 'react'

import {useMergeMethodContext} from '../contexts/MergeMethodContext'
import {useSuspenseMergeBoxPageData} from '../page-data/loaders/use-merge-box-page-data'
import {useStatusChecksPageData} from '../page-data/loaders/use-status-checks-page-data'
import {useMergeabilityLiveUpdates, type MergeabilityLiveUpdateChannels} from './use-mergeability-live-updates'
import {useStatusChecksLiveUpdates} from './use-status-checks-live-updates'
import {MergeBoxStatusCalculator} from '../helpers/merge-box-status-calculator/merge-box-status-calculator'
import {useFeatureStates} from '@github-ui/pull-requests/use-feature-state'

const EMPTY_CHANNELS: MergeabilityLiveUpdateChannels = {
  stateChannel: null,
  deployedChannel: null,
  reviewStateChannel: null,
  workflowsChannel: null,
  mergeQueueChannel: null,
  headRefChannel: null,
  baseRefChannel: null,
  gitMergeStateChannel: null,
  pullRequestChannel: null,
  codeScanningChannel: null,
}

export function useMergeboxStatusCalculator({
  headSha,
  channels,
}: {
  headSha: string
  channels: MergeabilityLiveUpdateChannels | null
}) {
  const {mergeMethod} = useMergeMethodContext()
  const {data, refetch} = useSuspenseMergeBoxPageData({mergeMethod})
  const {
    data: statusChecksData,
    data: {aliveChannels},
  } = useStatusChecksPageData({pullRequestHeadSha: headSha})

  const refetchQuery = useCallback(() => {
    startTransition(() => {
      refetch({cancelRefetch: false})
    })
  }, [refetch])

  useMergeabilityLiveUpdates({refetchQuery, channels: channels ?? EMPTY_CHANNELS})
  useStatusChecksLiveUpdates(aliveChannels.commitHeadShaChannel, headSha)

  const {pullRequest, mergeRequirements} = data
  const featureStates = useFeatureStates()

  const calculator = useMemo(
    () => new MergeBoxStatusCalculator(pullRequest, mergeRequirements, statusChecksData, featureStates),
    [pullRequest, mergeRequirements, statusChecksData, featureStates],
  )

  return {calculator, pullRequest, mergeRequirements, statusChecksData}
}
